/**
 * Tab computation utility for entity detail pages.
 *
 * Groups linked entities by schema_id and maps them to tab definitions
 * using the shared schemaVisual() registry.
 */

import type { IconName } from "../../components/ui/Icon";
import type { LinkedEntitySummary } from "./sharedTypes";
import { schemaVisual, schemaTabLabel } from "./schemaIcons";

/** A group of entities of the same type within a module tab */
export interface EntityTypeGroup {
  readonly schemaId: string;
  /** Subheading label, e.g. "Chats", "Messages", "Triggers" */
  readonly label: string;
  readonly icon: IconName;
  readonly entityIds: readonly string[];
}

export interface DynamicTab {
  readonly id: string;
  /** Tab label = module title (e.g. "Telegram") */
  readonly label: string;
  readonly icon: IconName;
  readonly count: number;
  readonly entityIds: readonly string[];
  /** Entity type groups within this tab (e.g. Chats, Messages) */
  readonly groups: readonly EntityTypeGroup[];
}

export interface FixedTab {
  readonly id: string;
  readonly label: string;
}

export const FIXED_TABS: readonly FixedTab[] = [
  { id: "description", label: "Description" },
  { id: "memory", label: "Memory" },
];

/**
 * Compute dynamic tabs from linked entities.
 * Groups by MODULE (schema_id prefix before dot), tab label = module title from registry.
 *
 * @param moduleTitles — map of moduleId → title from runtime.modules (e.g. { telegram: "Telegram" })
 */
export function computeDynamicTabs(
  linkedEntities: readonly LinkedEntitySummary[],
  moduleTitles: Readonly<Record<string, string>>,
): readonly DynamicTab[] {
  // First pass: group by moduleId, then by schemaId within module
  const moduleGroups = new Map<string, {
    icon: IconName;
    schemaGroups: Map<string, { ids: string[]; label: string; icon: IconName }>;
  }>();

  for (const entity of linkedEntities) {
    const schemaId = entity.schema_id;

    // Skip notes (handled separately), individual messages (too many,
    // noise), and email.address linked entities — those are surfaced
    // in the Details tab as Google-Contacts-style rows, not as a
    // separate "Addresses" sub-section under EMAILS.
    if (schemaId === "notes.note" || schemaId === "telegram.message" || schemaId === "email.address") continue;

    const visual = schemaVisual(schemaId);
    const moduleId = visual.moduleId;

    if (!moduleGroups.has(moduleId)) {
      moduleGroups.set(moduleId, { icon: visual.icon, schemaGroups: new Map() });
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const mod = moduleGroups.get(moduleId)!;

    if (!mod.schemaGroups.has(schemaId)) {
      // Pluralize schema label for subheading (e.g. "Chat" → "Chats")
      const label = schemaTabLabel(schemaId);
      mod.schemaGroups.set(schemaId, { ids: [], label, icon: visual.icon });
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    mod.schemaGroups.get(schemaId)!.ids.push(entity.id);
  }

  const tabs: DynamicTab[] = [];
  for (const [moduleId, mod] of moduleGroups) {
    const allIds: string[] = [];
    const groups: EntityTypeGroup[] = [];

    for (const [schemaId, sg] of mod.schemaGroups) {
      allIds.push(...sg.ids);
      groups.push({
        schemaId,
        label: sg.label,
        icon: sg.icon,
        entityIds: sg.ids,
      });
    }

    tabs.push({
      id: moduleId,
      label: moduleTitles[moduleId] ?? moduleId.charAt(0).toUpperCase() + moduleId.slice(1),
      icon: mod.icon,
      count: allIds.length,
      entityIds: allIds,
      groups,
    });
  }

  // Sort by count descending
  tabs.sort((a, b) => b.count - a.count);

  return tabs;
}

/**
 * Build the full tab list: fixed tabs first, then dynamic tabs.
 */
export function buildAllTabs(
  dynamicTabs: readonly DynamicTab[],
): readonly { readonly id: string; readonly label: string }[] {
  return [
    ...FIXED_TABS,
    ...dynamicTabs.map((t) => ({ id: t.id, label: t.label })),
  ];
}
